import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from "vue";
  import Modal from "@/components/ui/Modal.vue";
  import FormModalLayout from "./FormModalLayout.vue";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import store from "@/store";
  import { StageInterface } from "@/types/StageInterface";
  import { useToast } from "vue-toastification";

  type StageContactCount = {
    stageId: string;
    contactsCount: number;
  };

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ExportContactsModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    estateId: {
      type: String,
      required: true,
    },
  },
  emits: ["close"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();

  const props = __props;

  const emit = __emit;

  const loading = ref(false);
  const exporting = ref(false);
  const stages = ref<StageInterface[]>([]);
  const stageContactCounts = ref<Record<string, number>>({});
  const selectedStageIds = ref<Record<string, boolean>>({});

  const hasSelection = computed(() =>
    Object.values(selectedStageIds.value).some(Boolean)
  );

  const selectableStageIds = computed(() =>
    stages.value
      .filter((s) => getStageContactsCount(s._id) > 0)
      .map((s) => s._id)
  );

  const isAllSelected = computed(
    () =>
      selectableStageIds.value.length > 0 &&
      selectableStageIds.value.every((id) => selectedStageIds.value[id])
  );

  const isSomeSelected = computed(() =>
    selectableStageIds.value.some((id) => selectedStageIds.value[id])
  );

  function toggleAll() {
    const newVal = !isAllSelected.value;
    const updated = { ...selectedStageIds.value };

    for (const id of selectableStageIds.value) {
      updated[id] = newVal;
    }

    selectedStageIds.value = updated;
  }

  watch(
    () => props.isOpen,
    (open) => {
      if (open) {
        loadData();
      }
    }
  );

  async function loadData() {
    loading.value = true;

    try {
      const [stagesRes, stageCountsRes] = await Promise.all([
        store.dispatch("stage/getStages", { estateId: props.estateId }),
        store.dispatch("contact/getContactStageCounts", props.estateId),
      ]);

      stages.value = stagesRes.data;
      stageContactCounts.value = stageCountsRes.data.reduce(
        (counts: Record<string, number>, item: StageContactCount) => {
          counts[item.stageId.toString()] = item.contactsCount;
          return counts;
        },
        {}
      );

      const selection: Record<string, boolean> = {};

      for (const stage of stages.value) {
        selection[stage._id] = getStageContactsCount(stage._id) > 0;
      }

      selectedStageIds.value = selection;
    } catch {
      toast.error("Failed to load stages");
    } finally {
      loading.value = false;
    }
  }

  async function onExport() {
    const chosenIds = Object.entries(selectedStageIds.value)
      .filter(([, checked]) => checked)
      .map(([id]) => id);

    if (chosenIds.length === 0) return;

    exporting.value = true;

    try {
      const res = await store.dispatch("contact/exportContacts", {
        estateId: props.estateId,
        stageIds: chosenIds,
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `contacts-export-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      link.click();
      URL.revokeObjectURL(url);

      emit("close");
    } catch {
      toast.error("Failed to export contacts");
    } finally {
      exporting.value = false;
    }
  }

  function onClose() {
    emit("close");
  }

  function getStageContactsCount(stageId: string) {
    return stageContactCounts.value[stageId] || 0;
  }

  function formatContactsCount(count: number) {
    return `${count} ${count === 1 ? "Contact" : "Contacts"}`;
  }

const __returned__ = { toast, props, emit, loading, exporting, stages, stageContactCounts, selectedStageIds, hasSelection, selectableStageIds, isAllSelected, isSomeSelected, toggleAll, loadData, onExport, onClose, getStageContactsCount, formatContactsCount, computed, ref, watch, Modal, FormModalLayout, CheckboxInput, get store() { return store }, get StageInterface() { return StageInterface }, get useToast() { return useToast } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})